import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const STATUS_STYLE = {
  Pending: "bg-amber-50 text-amber-700",
  "Pending Approval": "bg-amber-50 text-amber-700",
  Approved: "bg-emerald-50 text-emerald-700",
  Rejected: "bg-rose-50 text-rose-700",
  Cancelled: "bg-slate-100 text-slate-500",
};

const StatusBadge = ({ status }) => (
  <Badge className={`${STATUS_STYLE[status] || "bg-slate-100 text-slate-600"} text-[10px] hover:opacity-100`}>{status || "—"}</Badge>
);

function detailsOf(r) {
  switch (r.type) {
    case "leave":
      return [r.leave_type, `${fmtDate(r.start_date)}${r.end_date ? " → " + fmtDate(r.end_date) : ""}`, r.days ? `${r.days} day(s)` : null, r.half_day ? "Halfday" : null].filter(Boolean).join(" · ");
    case "official_business":
      return [fmtDate(r.target_date), r.location, r.person_to_see ? `See: ${r.person_to_see}` : null].filter(Boolean).join(" · ");
    case "wfh":
      return `${fmtDate(r.date_from)}${r.date_to ? " → " + fmtDate(r.date_to) : ""}`;
    case "overtime":
      return [fmtDate(r.target_date), r.hours ? `${r.hours}h` : null, r.start_time && r.end_time ? `${r.start_time}–${r.end_time}` : null].filter(Boolean).join(" · ");
    case "dtr_correction":
      return [fmtDate(r.target_date), r.dtr_type, r.correct_time_in ? `In: ${r.correct_time_in}` : null, r.correct_time_out ? `Out: ${r.correct_time_out}` : null].filter(Boolean).join(" · ");
    default:
      return "—";
  }
}

function TkCard({ title, rows, testid }) {
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.employee_name, r.employee_no, r.approver, r.status, detailsOf(r)]
        .filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / perPage));
  const curPage = Math.min(page, pages - 1);
  const slice = filtered.slice(curPage * perPage, curPage * perPage + perPage);
  const from = total === 0 ? 0 : curPage * perPage + 1;
  const to = Math.min(total, curPage * perPage + perPage);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testid}>
      <div className="text-base font-semibold text-slate-900 mb-4">{title}</div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <label className="text-sm text-slate-500 flex items-center gap-2">
          Show
          <select
            className="border border-slate-200 rounded-md h-8 px-2 text-sm text-slate-700 bg-white"
            value={perPage}
            onChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }}
            data-testid={`${testid}-per-page`}
          >
            {[10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          entries
        </label>
        <label className="text-sm text-slate-500 flex items-center gap-2">
          Search:
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="h-8 w-44"
            data-testid={`${testid}-search`}
          />
        </label>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-100">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-[32%]">Employee</TableHead>
              <TableHead>Details</TableHead>
              <TableHead className="w-[22%]">Approver</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid={`${testid}-body`}>
            {slice.length === 0 && (
              <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-6 text-sm">No data available in table</TableCell></TableRow>
            )}
            {slice.map((r) => (
              <TableRow key={r.id} data-testid={`${testid}-row`}>
                <TableCell className="align-top">
                  <div className="text-sm font-medium text-slate-800">{r.employee_name || "—"}</div>
                  {r.employee_no && <div className="text-xs text-slate-400 font-mono">{r.employee_no}</div>}
                  {r.department && <div className="text-xs text-slate-400">{r.department}</div>}
                </TableCell>
                <TableCell className="align-top">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-slate-700">{detailsOf(r) || "—"}</span>
                    <StatusBadge status={r.status} />
                  </div>
                  {r.reason && <div className="text-xs text-slate-400 mt-1 italic">“{r.reason}”</div>}
                </TableCell>
                <TableCell className="align-top text-sm text-slate-600">{r.approver || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 mt-3">
        <div className="text-sm text-slate-500">Showing {from} to {to} of {total} entries</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={curPage === 0} onClick={() => setPage(curPage - 1)} data-testid={`${testid}-prev`}>Previous</Button>
          <Button variant="outline" size="sm" disabled={curPage >= pages - 1} onClick={() => setPage(curPage + 1)} data-testid={`${testid}-next`}>Next</Button>
        </div>
      </div>
    </div>
  );
}

export default function Timekeeping() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState({ leaves: [], official_business: [], wfh: [], overtime: [], dtr: [] });

  const load = useCallback((s = start, e = end) => {
    const qp = new URLSearchParams();
    if (s) qp.set("start", s);
    if (e) qp.set("end", e);
    api.get(`/hris/timekeeping?${qp.toString()}`).then(({ data }) => setData(data)).catch(() => {});
  }, [start, end]);

  useEffect(() => { load("", ""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="uemp-fade-in">
      <PageHeader title="Timekeeping Dashboard" subtitle="Monitor leave, official business, WFH, overtime and DTR entries across your company." />

      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-5" data-testid="timekeeping-filter">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <div className="text-xs text-slate-500">From</div>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-44" data-testid="timekeeping-from" />
          </div>
          <div className="space-y-1.5">
            <div className="text-xs text-slate-500">To</div>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-44" data-testid="timekeeping-to" />
          </div>
          <Button onClick={() => load()} className="bg-blue-600 hover:bg-blue-700 h-10 px-8" data-testid="timekeeping-filter-btn">Filter</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <TkCard title="Leaves" rows={data.leaves} testid="tk-leaves" />
        <TkCard title="Official Business" rows={data.official_business} testid="tk-ob" />
        <TkCard title="Work From Home" rows={data.wfh} testid="tk-wfh" />
        <TkCard title="Overtime" rows={data.overtime} testid="tk-overtime" />
        <TkCard title="DTR Approvals" rows={data.dtr} testid="tk-dtr" />
      </div>
    </div>
  );
}
